import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, BookOpen, FileBarChart, Gavel, HeartHandshake, School, ShieldAlert, TrendingUp, UserCheck, type LucideIcon } from "lucide-react";

import { disciplineService } from "../services/disciplineService";
import { usePermissions } from "../hooks/usePermissions";
import { useAuthStore } from "../stores/authStore";
import { todayIso } from "../utils/dates";
import { DefinitionsPanel } from "../components/discipline/DefinitionsPanel";
import { IncidentsPanel } from "../components/discipline/IncidentsPanel";
import { ClassesPanel, PatternsPanel, ReportsPanel } from "../components/discipline/InsightPanels";
import { PlansPanel } from "../components/discipline/PlansPanel";
import { awaitsApproval, isOpenCase, reviewDue } from "../components/discipline/disciplineLogic";

type Tab = "incidents" | "plans" | "patterns" | "classes" | "reports" | "definitions";

/** Conduct: cases from report to close, learner support plans, patterns and reports. */
export default function Disciplinary() {
  const { hasCapability } = usePermissions();
  const isReviewer = hasCapability("access.discipline.reviewer");
  const isLeadership = hasCapability("access.discipline.leadership");
  const me = useAuthStore((state) => state.user?.id);
  const tabs: { id: Tab; label: string; icon: LucideIcon }[] = [
    { id: "incidents", label: "Incidents", icon: ShieldAlert },
    { id: "plans", label: "Support plans", icon: HeartHandshake },
    { id: "patterns", label: "Patterns", icon: TrendingUp },
    { id: "classes", label: "By class", icon: School },
    ...(isReviewer ? [{ id: "reports" as const, label: "Reports", icon: FileBarChart }] : []),
    { id: "definitions", label: "Definitions", icon: BookOpen },
  ];
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "incidents";
  const view = params.get("view") ?? undefined;
  const select = (next: Tab, nextView?: string) => {
    const query: Record<string, string> = {};
    if (next !== "incidents") query.tab = next;
    if (nextView) query.view = nextView;
    setParams(query, { replace: true });
  };

  const queryClient = useQueryClient();
  const incidents = useQuery({ queryKey: ["discipline", "incidents"], queryFn: disciplineService.getBehaviorIncidents });
  const plans = useQuery({ queryKey: ["discipline", "plans"], queryFn: disciplineService.getInterventionPlans });
  const patterns = useQuery({ queryKey: ["discipline", "patterns"], queryFn: disciplineService.getBehaviorPatterns });
  const classes = useQuery({ queryKey: ["discipline", "classes"], queryFn: disciplineService.getClassDisciplinarySummary, enabled: tab === "classes" });
  const summary = useQuery({ queryKey: ["discipline", "summary"], queryFn: disciplineService.getDisciplinarySummary });
  const categories = useQuery({ queryKey: ["discipline", "categories"], queryFn: disciplineService.getBehaviorCategories });
  const sanctions = useQuery({ queryKey: ["discipline", "sanctions"], queryFn: disciplineService.getDisciplinaryActions });
  const learners = useQuery({ queryKey: ["discipline", "learners"], queryFn: disciplineService.getStudents, staleTime: 5 * 60_000 });
  const staff = useQuery({ queryKey: ["discipline", "staff"], queryFn: disciplineService.getStaff, staleTime: 5 * 60_000 });
  const reports = useQuery({ queryKey: ["discipline", "reports"], queryFn: disciplineService.getDisciplinaryReports, enabled: isReviewer });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["discipline"] });

  const today = todayIso();
  const cases = incidents.data ?? [];
  const open = cases.filter(isOpenCase);
  const escalated = open.filter((row) => row.status === "escalated").length;
  const toApprove = cases.filter((row) => awaitsApproval(row) && isOpenCase(row) && row.reported_by !== me).length;
  const mineOpen = open.filter((row) => row.reported_by === me).length;
  const activePlans = (plans.data ?? []).filter((row) => row.status === "active");
  const reviews = activePlans.filter((row) => reviewDue(row, today)).length;
  const needSupport = new Set((patterns.data ?? []).filter((row) => row.intervention_required).map((row) => row.student)).size;
  const recent = summary.data?.incidents_this_month ?? 0;
  const before = summary.data?.incidents_last_month ?? 0;
  const dash = (query: { isLoading: boolean }, text: string) => (query.isLoading ? "–" : text);

  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    { label: "Open cases", value: dash(incidents, String(open.length)), note: escalated ? `${escalated} escalated` : "None escalated", go: () => select("incidents", "open"), icon: ShieldAlert, alert: escalated > 0 },
    isLeadership
      ? { label: "Waiting for your approval", value: dash(incidents, String(toApprove)), note: toApprove ? "Proposed actions to decide" : "Nothing to decide", go: () => select("incidents", "approval"), icon: Gavel, alert: toApprove > 0 }
      : { label: "My open reports", value: dash(incidents, String(mineOpen)), note: "Cases you reported, still open", go: () => select("incidents", "mine"), icon: UserCheck },
    { label: "Active support plans", value: dash(plans, String(activePlans.length)), note: reviews ? `${reviews} review${reviews === 1 ? "" : "s"} due` : "No reviews due", go: () => select("plans", reviews ? "review" : "active"), icon: HeartHandshake, alert: reviews > 0 },
    { label: "Cases in the last 30 days", value: dash(summary, String(recent)), note: `${before} in the 30 days before`, go: () => select("incidents", "all"), icon: BarChart3, alert: recent > before && before > 0 },
  ];
  const badges: Partial<Record<Tab, number>> = { incidents: isLeadership ? toApprove : 0, plans: reviews, patterns: needSupport };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Discipline</h1>
        <p className="mt-1 text-sm text-gray-500">
          {isReviewer
            ? "Conduct cases from report to close, support plans that help learners change course, and the patterns behind them."
            : "Report an incident, follow the cases you reported or that involve your class, and work on the support plans you are part of."}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={tile.go}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Discipline sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = badges[item.id];
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "incidents" && (
        <IncidentsPanel
          incidents={incidents.data}
          learners={learners.data ?? []}
          categories={categories.data ?? []}
          sanctions={sanctions.data ?? []}
          isLoading={incidents.isLoading}
          isError={incidents.isError}
          onRetry={() => incidents.refetch()}
          me={me}
          isReviewer={isReviewer}
          isLeadership={isLeadership}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "plans" && (
        <PlansPanel
          plans={plans.data}
          learners={learners.data ?? []}
          staff={staff.data ?? []}
          isLoading={plans.isLoading}
          isError={plans.isError}
          onRetry={() => plans.refetch()}
          me={me}
          isReviewer={isReviewer}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "patterns" && <PatternsPanel patterns={patterns.data} isLoading={patterns.isLoading} isError={patterns.isError} onRetry={() => patterns.refetch()} onPlan={() => select("plans")} />}
      {tab === "classes" && <ClassesPanel classes={classes.data} isLoading={classes.isLoading} isError={classes.isError} onRetry={() => classes.refetch()} />}
      {tab === "reports" && isReviewer && (
        <ReportsPanel reports={reports.data} learners={learners.data ?? []} categories={categories.data ?? []} isLoading={reports.isLoading} isError={reports.isError} onRetry={() => reports.refetch()} onChanged={refresh} />
      )}
      {tab === "definitions" && (
        <DefinitionsPanel
          categories={categories.data}
          sanctions={sanctions.data}
          isLoading={categories.isLoading || sanctions.isLoading}
          isError={categories.isError || sanctions.isError}
          onRetry={() => { categories.refetch(); sanctions.refetch(); }}
          canEdit={isLeadership}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
